import type {
  OrderGetOneData,
  ResponseBaseGetOneResponseProgramPage,
  AppSchemasProgrammingSessionProgramPageSession as Session,
} from "@classes-service-types/index.ts";
import type { TodosSubmissions, TodosSubmissionsListResponse } from "@profile-service-types/index.ts";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import { useRoute } from "vue-router";
import type { ProgramPageSessionsData } from "@/components/MyStudents/Submissions/types.ts";
import { hasTodoViewSubmissionPermission } from "@/composables/authorization.js";
import ordersService from "@/services/orders.js";
import programsPageService from "@/services/programsPage.js";
import todosSubmissionsService from "@/services/todosSubmissions.js";

export const TODO_SUBMISSION_QUERY_KEY_PREFIX = "todoSubmission";

export const useSubmissionReviewData = () => {
  const route = useRoute();

  const applicationId = computed(() => route.params.applicationId as string);
  const todoId = computed(() => route.params.todoId as string);

  const { data: orderData, isPending: orderIsPending } = useQuery({
    queryKey: ["order", applicationId],
    queryFn: async (): Promise<OrderGetOneData | null> => {
      const orderResponse: OrderGetOneData[] = await ordersService.list({ application_id: applicationId.value });
      return orderResponse?.[0] ?? null;
    },
    enabled: computed(() => !!hasTodoViewSubmissionPermission.value),
  });

  const participantId = computed(() => orderData.value?.order?.participant_id);
  const participantName = computed(() => orderData.value?.order?.participant_fullname);
  const participantEmail = computed(() => orderData.value?.order?.participant_email);
  const participantFirstName = computed(() => orderData.value?.order?.participant_first_name);
  const participantLastName = computed(() => orderData.value?.order?.participant_last_name);
  const orderId = computed(() => orderData.value?.order?.id);
  const programId = computed(() => orderData.value?.order?.program_page_id);
  const sessionId = computed(() => orderData.value?.order?.session_id);

  const { data: programData, isPending: programIsPending } = useQuery({
    queryKey: ["program", programId],
    queryFn: async (): Promise<ProgramPageSessionsData | null> => {
      const response: { data?: ResponseBaseGetOneResponseProgramPage } = await programsPageService.getProgramPage(
        programId.value,
      );
      return (response?.data?.data?.items as ProgramPageSessionsData | undefined) ?? null;
    },
    enabled: computed(() => !!programId.value),
  });

  const sessionData = computed(() =>
    programData.value?.sessions?.find((session: Session) => session.id === sessionId.value),
  );

  const {
    data: todoSubmissionsData,
    isPending: todoSubmissionIsPending,
    isError: isTodoSubmissionError,
  } = useQuery({
    queryKey: [TODO_SUBMISSION_QUERY_KEY_PREFIX, applicationId, participantId],
    queryFn: async (): Promise<TodosSubmissions[]> => {
      const response: { data?: TodosSubmissionsListResponse } = await todosSubmissionsService.getTodoSubmission({
        id: participantId.value,
        application_ids: applicationId.value,
        limit: 100,
        get_archived: true,
      });
      return response?.data?.items ?? [];
    },
    enabled: computed(() => !!participantId.value),
  });

  const todoSubmissionData = computed<TodosSubmissions | null>(
    () => todoSubmissionsData.value?.find((todo: TodosSubmissions) => todo.todo_id === todoId.value) ?? null,
  );

  const isLoading = computed(
    () =>
      orderIsPending.value ||
      (!!participantId.value && todoSubmissionIsPending.value) ||
      (!!programId.value && programIsPending.value),
  );

  return {
    applicationId,
    todoId,
    participantId,
    participantName,
    participantEmail,
    participantFirstName,
    participantLastName,
    orderId,
    programData,
    sessionData,
    todoSubmissionData,
    isLoading,
    isTodoSubmissionError,
  };
};
